"use client";

import { useEffect, useMemo, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";

const SCRIPT_SRC_BASE = "https://app.termly.io";

type TermlyCMPProps = {
  autoBlock?: boolean;
  masterConsentsOrigin?: string;
  websiteUUID?: string;
  dataId?: string;
};

declare global {
  interface Window {
    Termly?: {
      initialize: () => void; // Match the existing type
    };
  }
}

export default function TermlyCMP({
  autoBlock = false,
  masterConsentsOrigin,
  websiteUUID,
}: TermlyCMPProps): null {
  const scriptSrc = useMemo(() => {
    const src = new URL(SCRIPT_SRC_BASE);
    src.pathname = `/resource-blocker/${websiteUUID}`;
    if (autoBlock) {
      src.searchParams.set("autoBlock", "on");
    }
    if (masterConsentsOrigin) {
      src.searchParams.set("masterConsentsOrigin", masterConsentsOrigin);
    }
    return src.toString();
  }, [autoBlock, masterConsentsOrigin, websiteUUID]);

  const isScriptAdded = useRef(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (typeof window !== "undefined") {
      const nonce =
        document.cookie
          .split("; ")
          .find((row) => row.startsWith("nonce="))
          ?.split("=")[1] || "";

      if (isScriptAdded.current) return;

      const script = document.createElement("script");
      script.src = scriptSrc;
      if (nonce) {
        script.setAttribute("nonce", nonce);
      }
      script.async = true;

      script.onload = () => {
        if (window.Termly) {
          window.dispatchEvent(new Event("termlyScriptLoaded")); // ✅ Dispatch only if Termly exists
        }
      };

      script.onerror = () => {
        console.error("Failed to load Termly script");
      };

      document.head.appendChild(script);
      isScriptAdded.current = true;
    }
  }, [scriptSrc]);

  // ✅ Wait until Termly is available before calling `initialize()`
  useEffect(() => {
    const checkAndInitialize = () => {
      if (window.Termly?.initialize) {
        window.Termly.initialize();
      } else {
        console.warn(
          "Termly object or initialize function is not available yet."
        );
      }
    };

    if (window.Termly) {
      checkAndInitialize();
    } else {
      window.addEventListener("termlyScriptLoaded", checkAndInitialize);
    }

    return () => {
      window.removeEventListener("termlyScriptLoaded", checkAndInitialize);
    };
  }, [pathname, searchParams]);

  return null;
}
